// src/cockpit4/components/ContentQualityScorer.tsx
// ============================================
// P0-功能2：内容质量评分系统（媒体工作室）
//
// 功能：
//   1. 综合质量分（0-100）
//   2. 5+ 维度分项评分：标题吸引力/结构完整性/情感倾向/合规性/品牌一致性/爆款概率
//   3. 低分项自动给出优化建议
//   4. 一键优化（跳转媒体工作室改稿）
//   5. 合规性检测（敏感词/违规风险）
//
// 数据来源：
//   - MediaStudio.list_packages（已生成的脚本内容）
//   - 规则评分引擎（客观指标）
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gauge,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  Shield,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { MediaPackage } from "../../runtime/MediaStudio/types";
import { useCockpit4Store } from "../store";

// ============================================
// 类型定义
// ============================================

interface QualityDimension {
  name: string;
  score: number;
  maxScore: number;
  status: "good" | "warning" | "critical";
  detail: string;
  suggestion?: string;
}

interface ContentQuality {
  packageId: string;
  topic: string;
  overallScore: number;
  dimensions: QualityDimension[];
  complianceIssues: string[];
  viralPrediction: number;
}

// ============================================
// 敏感词库（基础版）
// ============================================

const SENSITIVE_WORDS = [
  "第一", "最", "绝对", "保证", "100%", "免费", "中奖", "点击领取",
  "暴力", "色情", "赌博", "诈骗", "传销", "违禁",
];

// ============================================
// 质量评分引擎
// ============================================

function scoreContent(content: string, topic: string): ContentQuality {
  const dimensions: QualityDimension[] = [];

  // 1. 标题吸引力（基于标题长度、是否有数字/疑问/情绪词）
  const titleLength = topic.length;
  const hasNumber = /\d/.test(topic);
  const hasQuestion = /[?？]/.test(topic);
  const hasEmotion = /震惊|惊呆|重磅|突发|揭秘|必看/.test(topic);

  let titleScore = 0;
  if (titleLength >= 10 && titleLength <= 30) titleScore += 30;
  else if (titleLength > 0) titleScore += 15;
  if (hasNumber) titleScore += 25;
  if (hasQuestion) titleScore += 20;
  if (hasEmotion) titleScore += 25;

  dimensions.push({
    name: "标题吸引力",
    score: Math.min(100, titleScore),
    maxScore: 100,
    status: titleScore >= 70 ? "good" : titleScore >= 40 ? "warning" : "critical",
    detail: `标题"${topic}"，长度${titleLength}字，${hasNumber ? "含数字" : "无数字"}，${hasQuestion ? "含疑问" : "无疑问"}，${hasEmotion ? "含情绪词" : "无情绪词"}`,
    suggestion: titleScore < 70 ? "建议：加入数字（如\"5个方法\"）、疑问句式或情绪触发词" : undefined,
  });

  // 2. 结构完整性（基于段落数、标题层级、字数）
  const paragraphs = content.split(/\n\n+/).filter((p) => p.trim().length > 0);
  const hasHeaders = /^#{1,3}\s/.test(content);
  const hasList = /^[-*]\s/m.test(content);
  const wordCount = content.length;

  let structureScore = 0;
  if (paragraphs.length >= 3) structureScore += 30;
  else if (paragraphs.length >= 1) structureScore += 15;
  if (hasHeaders) structureScore += 30;
  if (hasList) structureScore += 20;
  if (wordCount >= 500) structureScore += 20;
  else if (wordCount >= 200) structureScore += 10;

  dimensions.push({
    name: "结构完整性",
    score: Math.min(100, structureScore),
    maxScore: 100,
    status: structureScore >= 70 ? "good" : structureScore >= 40 ? "warning" : "critical",
    detail: `${paragraphs.length} 段落，${wordCount} 字，${hasHeaders ? "有标题层级" : "无标题层级"}，${hasList ? "有列表" : "无列表"}`,
    suggestion: structureScore < 70 ? "建议：增加段落分隔（至少3段）、使用标题层级（##/###）、加入列表要点" : undefined,
  });

  // 3. 情感倾向（基于正面/负面词频）
  const positiveWords = (content.match(/成功|优秀|创新|突破|价值|机会|成长|提升/g) || []).length;
  const negativeWords = (content.match(/失败|问题|风险|困难|挑战|不足|缺陷/g) || []).length;
  const sentimentScore = Math.min(100, Math.round((positiveWords / Math.max(1, positiveWords + negativeWords)) * 100));

  dimensions.push({
    name: "情感倾向",
    score: sentimentScore,
    maxScore: 100,
    status: sentimentScore >= 60 ? "good" : sentimentScore >= 30 ? "warning" : "critical",
    detail: `正面词${positiveWords}个，负面词${negativeWords}个，正面占比${sentimentScore}%`,
    suggestion: sentimentScore < 60 ? "建议：增加正面表述，强调价值和机会，减少纯负面描述" : undefined,
  });

  // 4. 合规性检测
  const foundSensitive = SENSITIVE_WORDS.filter((w) => content.includes(w));
  const complianceScore = Math.max(0, 100 - foundSensitive.length * 25);

  dimensions.push({
    name: "合规性",
    score: complianceScore,
    maxScore: 100,
    status: complianceScore >= 80 ? "good" : complianceScore >= 50 ? "warning" : "critical",
    detail: foundSensitive.length === 0 ? "未检测到敏感词" : `检测到敏感词：${foundSensitive.join("、")}`,
    suggestion: foundSensitive.length > 0 ? `建议：移除或替换敏感词：${foundSensitive.join("、")}` : undefined,
  });

  // 5. 品牌一致性（基于关键词出现）
  const brandKeywords = ["XEAOS", "企业操作系统", "AI", "智能", "自动化", "知识", "运营"];
  const brandMentions = brandKeywords.filter((k) => content.includes(k) || topic.includes(k));
  const brandScore = Math.min(100, brandMentions.length * 20);

  dimensions.push({
    name: "品牌一致性",
    score: brandScore,
    maxScore: 100,
    status: brandScore >= 60 ? "good" : brandScore >= 30 ? "warning" : "critical",
    detail: `品牌关键词出现${brandMentions.length}个：${brandMentions.join("、") || "无"}`,
    suggestion: brandScore < 60 ? "建议：增加品牌相关关键词（XEAOS、企业操作系统、AI、智能等）" : undefined,
  });

  // 6. 爆款概率预测（综合标题+情感+结构）
  const viralScore = Math.round(
    (titleScore * 0.35 + sentimentScore * 0.25 + structureScore * 0.2 + brandScore * 0.1 + (100 - foundSensitive.length * 25) * 0.1)
  );

  const overallScore = Math.round(
    dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length
  );

  return {
    packageId: "",
    topic,
    overallScore,
    dimensions,
    complianceIssues: foundSensitive,
    viralPrediction: viralScore,
  };
}

// ============================================
// 主组件
// ============================================

export default function ContentQualityScorer() {
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const [packages, setPackages] = useState<MediaPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [qualities, setQualities] = useState<ContentQuality[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    try {
      if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
        const resp = await RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" });
        const inner = resp?.data as any;
        if (inner?.success && Array.isArray(inner.data)) {
          const pkgs = inner.data as MediaPackage[];
          setPackages(pkgs);

          // 评分每个包
          const scored = pkgs.map((pkg) => {
            const scriptContent =
              pkg.articleScript?.content ||
              pkg.videoScript?.content ||
              pkg.xiaohongshuScript?.content ||
              pkg.bilibiliScript?.content ||
              pkg.douyinScript?.content ||
              "";
            const quality = scoreContent(scriptContent || pkg.description || "", pkg.topic || "未命名");
            quality.packageId = pkg.id;
            return quality;
          });
          setQualities(scored);

          if (scored.length > 0) {
            const avgScore = Math.round(scored.reduce((s, q) => s + q.overallScore, 0) / scored.length);
            setMessage({ type: "ok", text: `✅ 已评分 ${scored.length} 篇内容，平均质量分 ${avgScore}/100` });
          } else {
            setMessage({ type: "err", text: "暂无可评分的内容，请先生成内容" });
          }
        } else {
          setPackages([]);
          setQualities([]);
          setMessage({ type: "err", text: "暂无内容包" });
        }
      }
    } catch (err) {
      setMessage({ type: "err", text: `❌ 加载失败：${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  // 统计
  const stats = useMemo(() => {
    if (qualities.length === 0) return { avg: 0, high: 0, mid: 0, low: 0 };
    const avg = Math.round(qualities.reduce((s, q) => s + q.overallScore, 0) / qualities.length);
    const high = qualities.filter((q) => q.overallScore >= 70).length;
    const mid = qualities.filter((q) => q.overallScore >= 40 && q.overallScore < 70).length;
    const low = qualities.filter((q) => q.overallScore < 40).length;
    return { avg, high, mid, low };
  }, [qualities]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <Gauge size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">内容质量评分</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Quality Scorer · 平均 {stats.avg}/100 · 高质量 {stats.high} · 待优化 {stats.low}
            </p>
          </div>
        </div>
        <button
          onClick={loadPackages}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 size={11} className="text-cyan-400 animate-spin" /> : <RefreshCw size={11} className="text-cyan-400" />}
          <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">{loading ? "评分中..." : "重新评分"}</span>
        </button>
      </div>

      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className={`px-3 py-2 rounded-md border text-[10px] font-mono ${message.type === "ok" ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300" : "border-red-500/30 bg-red-500/[0.06] text-red-300"}`}>
            {message.text}
          </div>
        </div>
      )}

      <div className="p-4 space-y-2 max-h-[520px] overflow-y-auto">
        {qualities.length === 0 ? (
          <div className="text-center py-8">
            <Gauge size={24} className="mx-auto text-gray-600 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">暂无评分内容</p>
            <p className="text-[9px] font-mono text-gray-700 mt-1">生成内容后可进行质量评分</p>
          </div>
        ) : (
          qualities.map((q) => {
            const isExpanded = expandedId === q.packageId;
            const scoreColor = q.overallScore >= 70 ? "#10B981" : q.overallScore >= 40 ? "#F59E0B" : "#EF4444";
            return (
              <div key={q.packageId} className="rounded-md border bg-white/[0.02] overflow-hidden" style={{ borderColor: `${scoreColor}25` }}>
                <button onClick={() => setExpandedId(isExpanded ? null : q.packageId)} className="w-full p-2.5 text-left hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0 relative w-10 h-10">
                      <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40">
                        <circle cx="20" cy="20" r="17" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                        <circle cx="20" cy="20" r="17" fill="none" stroke={scoreColor} strokeWidth="3" strokeDasharray={`${(q.overallScore / 100) * 106.8} 106.8`} strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[11px] font-mono font-bold text-white">{q.overallScore}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-200 truncate">{q.topic}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] font-mono text-gray-600">爆款概率：{q.viralPrediction}%</span>
                        {q.complianceIssues.length > 0 && (
                          <span className="flex items-center gap-0.5 text-[9px] font-mono text-red-400">
                            <AlertTriangle size={8} />
                            {q.complianceIssues.length} 项合规问题
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown size={11} className="text-gray-600" /> : <ChevronRight size={11} className="text-gray-600" />}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden border-t border-white/[0.03]">
                      <div className="p-2.5 space-y-1.5">
                        {q.dimensions.map((d, i) => {
                          const dColor = d.status === "good" ? "#10B981" : d.status === "warning" ? "#F59E0B" : "#EF4444";
                          return (
                            <div key={i} className="rounded border border-white/[0.04] bg-white/[0.01] p-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-gray-300">{d.name}</span>
                                <span className="text-[10px] font-mono font-bold" style={{ color: dColor }}>{d.score}/{d.maxScore}</span>
                              </div>
                              <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden mb-1">
                                <div className="h-full rounded-full" style={{ width: `${d.score}%`, background: dColor }} />
                              </div>
                              <div className="text-[9px] font-mono text-gray-600">{d.detail}</div>
                              {d.suggestion && (
                                <div className="flex items-start gap-1 mt-1">
                                  <Lightbulb size={9} className="text-amber-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-[9px] text-gray-400">{d.suggestion}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {q.overallScore < 70 && (
                          <button onClick={() => setWorkspace("media_studio")} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors mt-1">
                            <ExternalLink size={9} />前往优化内容
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );
}
